import {useRef, useState} from 'react'
import {useGetUserSpecializations} from '@/features/users/queries/useGetUserSpecializations'
import {UserSpecialization} from '@/features/users/types'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {
    SelectComboboxInput,
    SelectComboboxList,
    SelectComboboxPopover,
    SelectItem,
    SelectTrigger
} from '@/components/ui/select-atoms/SelectAtoms'
import {InputStatusIcon} from '@/components/ui/input-status-icon/InputStatusIcon'
import {InputHelpText} from '@/components/ui/input-help-text/InputHelpText'
import {useTranslation} from '@/translations/i18n'
import {Skeleton} from '@/components/ui/skeleton/Skeleton'
import {ComboboxProvider, SelectProvider} from '@ariakit/react'
import {Label} from '@/components/ui/label/Label'

interface UserInputSelectSpecializationProps {
    label: string
    value?: UserSpecialization
    onChange: (value: UserSpecialization | null) => void
    placeholder?: string
    country?: string
    province?: string
    errorMessage?: string
    isPortal?: boolean
}

export const UserInputSelectSpecialization = ({
    label,
    placeholder,
    value,
    onChange,
    errorMessage,
    isPortal
}: UserInputSelectSpecializationProps) => {
    const {t} = useTranslation()
    const inputSelectSpecializationRef = useRef(null)
    const [isQueryEnabled, setIsQueryEnabled] = useState(false)

    // query
    const userSpecializationsQuery = useGetUserSpecializations(
        {
            limit: 100
        },
        {enabled: isQueryEnabled}
    )

    return (
        <ComboboxProvider setValue={userSpecializationsQuery.onSearch} resetValueOnHide>
            <SelectProvider
                value={value?.name ?? ''}
                setValue={value =>
                    onChange(
                        userSpecializationsQuery.remappedData.find(
                            userSpecialization => userSpecialization.name == value
                        ) ?? null
                    )
                }
                setOpen={isOpen => {
                    userSpecializationsQuery.onResetSearch(inputSelectSpecializationRef)
                    setIsQueryEnabled(isOpen)
                }}
            >
                <Flexbox direction="column" gap={1.5} fullWidth>
                    <Label>{label}</Label>
                    <SelectTrigger
                        placeholder={placeholder}
                        fullWidth
                        value={value?.name}
                        displayValue={() => value?.name}
                        hasError={!!errorMessage}
                    />
                    <Flexbox align="center" gap={errorMessage ? 1.5 : 0}>
                        <InputStatusIcon hasError={!!errorMessage} />
                        <InputHelpText error={errorMessage} />
                    </Flexbox>
                    <SelectComboboxPopover portal={!!isPortal} sameWidth>
                        <SelectComboboxInput
                            ref={inputSelectSpecializationRef}
                            placeholder={t('commons:search_placeholder')}
                        />
                        <SelectComboboxList>
                            {userSpecializationsQuery.isLoading ? (
                                <>
                                    {Array.from({length: 6}, (_, index) => (
                                        <Skeleton key={index} height={32} />
                                    ))}
                                </>
                            ) : userSpecializationsQuery.isError ? (
                                <InputHelpText error={t('errors:default')} />
                            ) : userSpecializationsQuery.remappedData.length > 0 ? (
                                userSpecializationsQuery.remappedData.map(specialization => (
                                    <SelectItem hasItemCheckbox key={specialization.id} value={specialization.name}>
                                        {specialization.name}
                                    </SelectItem>
                                ))
                            ) : (
                                <InputHelpText helpText={t('commons:no_results')} />
                            )}
                        </SelectComboboxList>
                    </SelectComboboxPopover>
                </Flexbox>
            </SelectProvider>
        </ComboboxProvider>
    )
}
